/**
 * The Prompt Library: every prompt as a card, filterable by category.
 *
 * Built to read as a sibling of the Roles Map directory — same card, same
 * role-coloured left border, same pill filters — so it looks like another
 * room of the same house rather than a separate product.
 *
 * Everyone can browse the cards. Opening one is a Smart Stack + benefit: the
 * cards come from `prompts.list`, which carries no prompt text, and the text
 * itself comes from `prompts.get`, which refuses anyone else. So the notice at
 * the top is the page being honest about what the server will do, not the
 * thing doing the gating.
 */

import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Loader2, Lock, Search, X } from "lucide-react";
import { PROMPT_CATEGORIES, type PromptCard, type PromptCategory } from "@shared/prompts";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import PromptsUpgradeNotice from "@/components/PromptsUpgradeNotice";
import { BUTTON } from "@/lib/buttonStyles";
import { ROLE_ICONS, ROLE_LABELS } from "@/lib/roleDisplay";
import { trpc } from "@/lib/trpc";

/* The same two tables as Home.tsx's ToolCard. Not imported from there because
   they are not exported, and a card that looks like a tool card should use
   the same colours for the same roles. */
const ROLE_BORDER_STYLES = {
  Atlas: "border-l-4 border-l-[oklch(0.32_0.08_255)]",
  Comet: "border-l-4 border-l-[oklch(0.55_0.12_70)]",
  Manus: "border-l-4 border-l-[oklch(0.38_0.09_155)]",
} as const;

const ROLE_BADGE_STYLES = {
  Atlas: "bg-[oklch(0.32_0.08_255/0.10)] text-[oklch(0.32_0.08_255)] border border-[oklch(0.32_0.08_255/0.25)]",
  Comet: "bg-[oklch(0.55_0.12_70/0.10)] text-[oklch(0.55_0.12_70)] border border-[oklch(0.55_0.12_70/0.25)]",
  Manus: "bg-[oklch(0.38_0.09_155/0.10)] text-[oklch(0.38_0.09_155)] border border-[oklch(0.38_0.09_155/0.25)]",
} as const;

const PILL =
  "inline-flex items-center min-h-11 px-4 text-sm font-medium rounded-full border transition-all duration-150";
const PILL_ON = "bg-foreground text-background border-foreground";
const PILL_OFF =
  "bg-transparent text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground";

export default function PromptLibrary() {
  const list = trpc.prompts.list.useQuery();
  const [category, setCategory] = useState<PromptCategory | null>(null);
  const [search, setSearch] = useState("");

  const prompts = list.data?.prompts ?? [];
  const access = list.data?.access;

  /* Only categories that have something in them. An empty pill is a button
     that answers "nothing here", which is a worse answer than not asking. */
  const categories = useMemo(
    () => PROMPT_CATEGORIES.filter(c => prompts.some(p => p.category === c)),
    [prompts]
  );

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return prompts.filter(p => {
      if (category && p.category !== category) return false;
      if (!q) return true;
      return [p.title, p.summary, p.bestFor, p.category].some(s => s.toLowerCase().includes(q));
    });
  }, [prompts, category, search]);

  const filtered = category !== null || search.trim() !== "";

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader context="Prompt Library" />

      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-[oklch(0.97_0.012_75)]" />
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="relative container py-8 md:py-12">
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
            The Prompt Library
          </p>
          <h1 className="font-['Fraunces'] text-3xl md:text-4xl font-bold text-foreground leading-tight mb-3 max-w-2xl">
            Ready-made prompts.{" "}
            <em className="not-italic text-primary">Just fill in the blanks.</em>
          </h1>
          <p className="text-base md:text-lg text-foreground/75 leading-relaxed max-w-2xl">
            Pick a prompt, answer a few short questions about your business,
            and copy the finished prompt into ChatGPT, Claude, Gemini or any AI
            you use. No editing brackets, no guessing what to type.
          </p>

          {access === "signed-out" || access === "upgrade" ? (
            <div className="mt-6 max-w-3xl">
              <PromptsUpgradeNotice access={access} />
            </div>
          ) : null}
        </div>
      </section>

      {/* Sticky from md up only. Six categories wrap to four rows on a
          phone, and a pinned bar that tall covered 40% of the screen while
          scrolling the cards it is meant to help find. */}
      <div className="md:sticky md:top-16 z-40 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="container py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
                aria-hidden="true"
              />
              <input
                type="search"
                placeholder="Search prompts..."
                aria-label="Search prompts"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full min-h-11 pl-9 pr-3 text-base bg-secondary border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground"
              />
            </div>

            <div className="hidden sm:block w-px h-5 bg-border" />

            <div className="flex items-center gap-1.5 flex-wrap" role="group" aria-label="Categories">
              <button
                type="button"
                onClick={() => setCategory(null)}
                aria-pressed={category === null}
                className={`${PILL} ${category === null ? PILL_ON : PILL_OFF}`}
              >
                All
              </button>
              {categories.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(category === c ? null : c)}
                  aria-pressed={category === c}
                  className={`${PILL} ${category === c ? PILL_ON : PILL_OFF}`}
                >
                  {c}
                </button>
              ))}
            </div>

            {filtered ? (
              <button
                type="button"
                onClick={() => {
                  setCategory(null);
                  setSearch("");
                }}
                className="ml-auto inline-flex items-center gap-1 min-h-11 px-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" aria-hidden="true" />
                Clear
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <main className="container py-10">
        <div className="mb-6">
          <h2 className="font-['Fraunces'] text-2xl font-bold text-foreground">
            {category ?? "All prompts"}
          </h2>
          {filtered && !list.isLoading ? (
            <p className="text-base text-muted-foreground mt-0.5">
              Showing <span className="font-semibold text-foreground">{shown.length}</span> of{" "}
              {prompts.length}.
            </p>
          ) : null}
        </div>

        {list.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin w-8 h-8 text-muted-foreground" aria-label="Loading" />
          </div>
        ) : list.isError ? (
          <p className="text-base text-foreground/75 py-10">
            We could not load the prompts just now. Please refresh the page to try again.
          </p>
        ) : shown.length === 0 ? (
          <div className="text-center py-16 px-6 bg-secondary/30 rounded-lg border border-border">
            <p className="font-['Fraunces'] text-2xl font-semibold text-foreground mb-3">
              No prompts found
            </p>
            <p className="text-base text-muted-foreground mb-6 max-w-md mx-auto">
              Nothing matched your search. Try a different word, or show every prompt.
            </p>
            <button
              type="button"
              onClick={() => {
                setCategory(null);
                setSearch("");
              }}
              className={BUTTON.primarySm}
            >
              Show all prompts
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {shown.map(p => (
              <PromptCardView key={p.slug} prompt={p} locked={access !== "member"} />
            ))}
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

function PromptCardView({ prompt, locked }: { prompt: PromptCard; locked: boolean }) {
  const primaryRole = prompt.roles[0] ?? "Atlas";

  return (
    <div
      className={`
        group bg-card rounded-lg border border-border ${ROLE_BORDER_STYLES[primaryRole]}
        shadow-[0_2px_8px_oklch(0.22_0.015_65/0.06)]
        card-lift flex flex-col h-full
      `}
    >
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-2">
          {prompt.category}
        </p>
        <h3 className="font-['Fraunces'] text-xl font-semibold text-foreground leading-snug mb-2">
          {prompt.title}
        </h3>

        <p className="text-base text-foreground/75 leading-relaxed mb-4 flex-1">{prompt.summary}</p>

        <p className="text-sm text-muted-foreground italic mb-4 border-t border-border pt-3">
          {prompt.bestFor}
        </p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {prompt.roles.map(role => (
            <span
              key={role}
              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_BADGE_STYLES[role]}`}
            >
              {ROLE_ICONS[role]} {ROLE_LABELS[role]}
            </span>
          ))}
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground border border-border">
            {prompt.youGet}
          </span>
        </div>

        {/* A link for everyone, locked or not. The prompt's own page is where
            the upgrade notice names this prompt, which is a better pitch than
            a disabled button on a card. */}
        <div className="flex items-center justify-end">
          <Link href={`/prompts/${prompt.slug}`} className={BUTTON.primarySm}>
            {locked ? <Lock className="w-4 h-4" aria-hidden="true" /> : null}
            Fill in this prompt
          </Link>
        </div>
      </div>
    </div>
  );
}
